import {
  marketCalendar,
  type SessionPhase,
  venueOfExchange,
} from '@alphadesk/ontology/calendar.ts';
import { MINUTE_MS, todayInIst } from '@alphadesk/ontology/when.ts';
import type { WireQuote } from '@alphadesk/web/contract.ts';
import { useMemo } from 'react';
import { useCalendar, venueNow } from './calendar.ts';
import { useNow } from './clock.ts';

export const MARKET_LENSES = ['all', 'nse', 'mcx'] as const;
export type MarketLens = (typeof MARKET_LENSES)[number];
export type Market = Exclude<MarketLens, 'all'>;

export const MARKET_LABEL: Record<MarketLens, string> = { all: 'All', nse: 'NSE', mcx: 'MCX' };

const marketOf = (exchange: string): Market =>
  venueOfExchange(exchange) === 'MCX' ? 'mcx' : 'nse';

/** A recommendation's market is its tracked contract's; a leg's, its own. */
export const exchangeOf = (row: { trackingExchange: string } | { exchange: string }): string =>
  'trackingExchange' in row ? row.trackingExchange : row.exchange;

export const inMarket = (lens: MarketLens, exchange: string): boolean =>
  lens === 'all' || marketOf(exchange) === lens;

export type SessionPhases = Record<Market, SessionPhase | null>;

function useMarketCalendar() {
  const { state } = useCalendar();
  const read = state.status === 'ok' ? state.data : null;
  return useMemo(() => (read === null ? null : marketCalendar(read.days)), [read]);
}

export function useMarketPhases(): SessionPhases {
  const calendar = useMarketCalendar();
  const now = useNow(MINUTE_MS);
  return useMemo(
    () =>
      calendar === null
        ? { nse: null, mcx: null }
        : { nse: venueNow(calendar, 'NFO', now).phase, mcx: venueNow(calendar, 'MCX', now).phase },
    [calendar, now],
  );
}

/** Until its market opens today, a quote's range is the last session's. */
export function useTodayRange(quote: WireQuote | undefined, exchange: string): WireQuote['day'] {
  const calendar = useMarketCalendar();
  const now = useNow(MINUTE_MS);
  const session = calendar?.session(venueOfExchange(exchange), todayInIst(now));
  const opened = session?.phases.some((one) => one.phase === 'NORMAL' && one.from <= now);
  return opened ? (quote?.day ?? null) : null;
}

export function tallyMarkets(exchanges: Iterable<string>): Record<Market, number> {
  const tally: Record<Market, number> = { nse: 0, mcx: 0 };
  for (const exchange of exchanges) tally[marketOf(exchange)] += 1;
  return tally;
}
